import { type Ref, ref } from 'vue'
import { submitHubspotForm } from './submitHubspotForm'

const DELAY_LOADING = 500

export default function useHubspotForm(
  $form: Ref<HTMLFormElement | undefined>,
  submitAction: string,
  options?: {
    validate?: () => boolean
    onSubmit?: (formData: Record<string, any>) => void
  },
) {
  const sending = ref(false)
  const avoidDoubleSubmit = ref(false)
  const success = ref(false)
  const processingError = ref('')
  const successMessage = ref('')

  async function submit() {
    if (!$form.value) return

    // Avoid double submit if still sending
    if (avoidDoubleSubmit.value) return

    if (options?.validate && !options.validate()) return

    avoidDoubleSubmit.value = true

    const timeout = setTimeout(() => {
      sending.value = true
    }, DELAY_LOADING)

    try {
      if (options?.onSubmit) {
        options.onSubmit(Object.fromEntries(new FormData($form.value)))
      }
      const result = await submitHubspotForm($form.value)
      if (result.inlineMessage) {
        successMessage.value = result.inlineMessage
        success.value = true
      } else if (result.redirectUri) {
        window.location = result.redirectUri
      } else {
        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message)
        } else {
          throw new Error('Error submitting form')
        }
      }
    } catch (e: any) {
      processingError.value = e.message
    } finally {
      clearTimeout(timeout)
      sending.value = false
    }

    sending.value = false
    avoidDoubleSubmit.value = false

    // send info to google analytics
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'hs_submission_successful',
        submitAction,
      })
    }
  }

  return { sending, success, processingError, successMessage, submit }
}
